// src/pages/motolivraison/commandes.page.jsx
//
// Liste de toutes les commandes MotoLivraison avec filtres puissants.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Table, message, Card, Row, Col, Tag, Tooltip, Breadcrumb, Statistic,
  Select, DatePicker, Button
} from 'antd';
import {
  EyeOutlined, RightOutlined, ContainerOutlined, ClockCircleOutlined,
  CheckCircleOutlined, PhoneOutlined, MobileOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { motolivraisonService } from '../../common/services';

// Couleurs/labels par statut
const STATUS_CONFIG = {
  en_attente_paiement: { color: 'amber', label: 'Attente paiement' },
  payee: { color: 'cyan', label: 'Payée' },
  en_attente_livreur: { color: 'orange', label: 'Attente livreur' },
  assignee: { color: 'blue', label: 'Assignée' },
  recuperee: { color: 'purple', label: 'Récupérée' },
  en_livraison: { color: 'geekblue', label: 'En livraison' },
  livree: { color: 'lime', label: 'Livrée' },
  confirmee: { color: 'green', label: 'Confirmée' },
  soldee: { color: 'green', label: 'Soldée' },
  annulee: { color: 'red', label: 'Annulée' },
};

const Commandes = () => {
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ statut: '', mode: '', dateDebut: null, dateFin: null });
  const navigate = useNavigate();

  const stats = {
    total: commandes.length,
    enAttente: commandes.filter(c => ['en_attente_paiement', 'en_attente_livreur'].includes(c.statut)).length,
    enCours: commandes.filter(c => ['assignee', 'recuperee', 'en_livraison'].includes(c.statut)).length,
    soldees: commandes.filter(c => c.statut === 'soldee').length,
  };

  const fetchCommandes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await motolivraisonService.listCommandes({
        statut: filters.statut || undefined,
        mode: filters.mode || undefined,
        dateDebut: filters.dateDebut ? filters.dateDebut.toISOString() : undefined,
        dateFin: filters.dateFin ? filters.dateFin.toISOString() : undefined,
      });
      setCommandes(data?.commandes || []);
    } catch (e) {
      message.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCommandes();
  }, [fetchCommandes]);

  const columns = [
    {
      title: 'Mode',
      dataIndex: 'mode',
      width: 80,
      align: 'center',
      render: (m) => (
        <Tag icon={m === 'app' ? <MobileOutlined /> : <PhoneOutlined />} color={m === 'app' ? 'blue' : 'purple'}>
          {m === 'app' ? 'App' : 'Tél'}
        </Tag>
      ),
    },
    {
      title: 'Commerce',
      dataIndex: ['commerceId', 'nom'],
      render: (_, r) => r.commerceId?.nom || '—',
    },
    {
      title: 'Client',
      key: 'client',
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 12 }}>{r.clientNom || (r.clientId?.firstName + ' ' + r.clientId?.lastName) || '—'}</div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.clientPhone}</div>
        </div>
      ),
    },
    {
      title: 'Livreur',
      key: 'livreur',
      render: (_, r) => r.livreurId
        ? <span style={{ fontSize: 12 }}>{r.livreurId.firstName} {r.livreurId.lastName}</span>
        : <span style={{ fontSize: 11, color: '#bfbfbf' }}>—</span>,
    },
    {
      title: 'Montant',
      dataIndex: 'montantTTC',
      width: 110,
      align: 'right',
      render: (v) => <strong>{v} F</strong>,
    },
    {
      title: 'Statut',
      dataIndex: 'statut',
      width: 140,
      render: (s) => {
        const cfg = STATUS_CONFIG[s] || { color: 'default', label: s };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Créée',
      dataIndex: 'createdAt',
      width: 140,
      render: (d) => d ? dayjs(d).format('DD/MM HH:mm') : '—',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_, r) => (
        <Tooltip title="Voir détail">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/dashboard/motolivraison/commandes/${r._id}`)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1" style={{ padding: '8px 24px' }}>
        <Breadcrumb separator={<RightOutlined />} style={{ marginBottom: 12 }}>
          <Breadcrumb.Item><Link to="/dashboard">Accueil</Link></Breadcrumb.Item>
          <Breadcrumb.Item>MotoLivraison</Breadcrumb.Item>
          <Breadcrumb.Item>Commandes</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={6}><Card><Statistic title="Total" value={stats.total} prefix={<ContainerOutlined />} /></Card></Col>
          <Col xs={6}><Card><Statistic title="En attente" value={stats.enAttente} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="En cours" value={stats.enCours} valueStyle={{ color: '#1890ff' }} /></Card></Col>
          <Col xs={6}><Card><Statistic title="Soldées" value={stats.soldees} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        </Row>

        <Card title="Commandes MotoLivraison">
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={6}>
              <Select
                placeholder="Statut"
                allowClear
                style={{ width: '100%' }}
                value={filters.statut || undefined}
                onChange={(v) => setFilters({ ...filters, statut: v || '' })}
                options={Object.entries(STATUS_CONFIG).map(([key, { label }]) => ({ value: key, label }))}
              />
            </Col>
            <Col xs={24} sm={6}>
              <Select
                placeholder="Mode"
                allowClear
                style={{ width: '100%' }}
                value={filters.mode || undefined}
                onChange={(v) => setFilters({ ...filters, mode: v || '' })}
                options={[{ value: 'app', label: 'App' }, { value: 'telephone', label: 'Téléphone' }]}
              />
            </Col>
            <Col xs={24} sm={12}>
              <DatePicker.RangePicker
                value={[filters.dateDebut, filters.dateFin]}
                onChange={(v) => setFilters({ ...filters, dateDebut: v?.[0] || null, dateFin: v?.[1] || null })}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
              />
            </Col>
          </Row>
          <Table
            columns={columns}
            dataSource={commandes.map(c => ({ ...c, key: c._id }))}
            loading={loading}
            pagination={{ pageSize: 20 }}
            scroll={{ x: 1100 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default Commandes;
